import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// Get API base URL based on current hostname
export function getApiBaseUrl(): string {
  if (process.env.REACT_APP_API_BASE_URL ) {
    return  process.env.REACT_APP_API_BASE_URL;
  }
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  return `${protocol}//${hostname}:8000`;
  // return 'https://7dad1fd85647.ngrok-free.app' // TODO (evan): better solution than this
}

/**
 * Fetch wrapper that shows errors in alert()
 */
async function fetchSafe(path: string, options?: RequestInit): Promise<Response> {
  const apiBaseUrl = getApiBaseUrl();
  const fullUrl = `${apiBaseUrl}${path}`;

  try {
    const response = await fetch(fullUrl, options);

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.message || errorMessage;
      } catch {
        // If response isn't JSON, use the status text
      }

      alert(`Error: ${errorMessage}`);
      throw new Error(errorMessage);
    }

    return response;
  } catch (error) {
    if (error instanceof Error) {
      // Network errors or other fetch errors
      alert(`Network Error: ${error.message}`);
    } else {
      alert('An unexpected error occurred');
    }
    throw error;
  }
}

// ======================= DownloadVideo ====================

export interface DownloadResponse {
  video_id: string;
}

export const useDownloadVideo = () => {
  const queryClient = useQueryClient();

  return useMutation<DownloadResponse, Error, { url: string }>({
    mutationFn: async ({ url }) => {
      const response = await fetchSafe('/videos/downloads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      });

      return response.json();
    },
    onSuccess: () => {
      // Invalidate video list to refresh it
      queryClient.invalidateQueries({ queryKey: ['videos'] });
    },
  });
};

// ======================= GetVideoStatus ====================

export type VideoStatus =
  | {
      status: 'ready';
      video_id: string;
      stream_url: string;
      storage_type: 's3' | 'local';
    }
  | {
      status: 'downloading';
      video_id: string;
      storage_type: 's3' | 'local';
    };

export const useVideoStatus = (videoId: string) => {
  return useQuery<VideoStatus>({
    queryKey: ['video-status', videoId],
    queryFn: async () => {
      const response = await fetchSafe(`/videos/${videoId}`);
      return response.json();
    },
    enabled: !!videoId,
    refetchInterval: (query) => {
      // Poll every 2 seconds if video is still downloading
      return query.state.data?.status === 'downloading' ? 2000 : false;
    },
    refetchIntervalInBackground: true,
  });
};

// ======================= ListVideos ====================

export interface Video {
  video_id: string;
  filename: string;
  size: number;
}

export interface VideoListResponse {
  videos: Video[];
}

export const useVideoList = () => {
  return useQuery<VideoListResponse>({
    queryKey: ['videos'],
    queryFn: async () => {
      const response = await fetchSafe('/videos');
      return response.json();
    },
  });
};

// ======================= Deprecated StreamVideo ====================

// export interface VideoStreamResponse {
//   stream_url: string;
//   storage_type: 's3' | 'local';
// }

// export const useVideoStream = (videoId: string) => {
//   return useQuery<VideoStreamResponse>({
//     queryKey: ['video-stream', videoId],
//     queryFn: async () => {
//       const response = await fetchSafe(`/videos/${videoId}`);
//       return response.json();
//     },
//     enabled: !!videoId,
//   });
// };
